<template>
  <header class="app-header" :class="{ 'header-transparent': transparent }">
    <div class="container header-inner">
      <router-link to="/" class="logo">
        <img src="/images/logo-dark.png" class="logo-img" alt="logo" />
        <span>Uni Link International Freight</span>
      </router-link>

      <nav class="main-nav">
        <router-link v-for="item in navItems" :key="item.path" :to="item.path"
          :class="{ active: $route.path === item.path }">
          {{ item.name }}
        </router-link>
      </nav>

      <div class="header-actions">
        <template v-if="!isLoggedIn">
          <router-link to="/login" class="login-btn">Login / Register</router-link>
        </template>
        <template v-else>
          <el-dropdown trigger="click" @command="handleCommand">
            <div class="user-info">
              <el-avatar :size="32" :src="userInfo.avatar" icon="el-icon-user-solid"></el-avatar>
              <span>{{ userInfo.nickname || userInfo.name || userInfo.mobile }}</span>
              <i class="el-icon-arrow-down"></i>
            </div>
            <el-dropdown-menu slot="dropdown">
              <el-dropdown-item command="profile">
                <div class="dropdown-user">
                  <el-avatar :size="40" :src="userInfo.avatar" icon="el-icon-user-solid"></el-avatar>
                  <div>
                    <p class="name">{{ userInfo.nickname || userInfo.name || userInfo.mobile }}</p>
                    <p class="edit"><i class="el-icon-edit"></i> Edit Profile</p>
                  </div>
                </div>
              </el-dropdown-item>
              <el-dropdown-item divided command="logout">
                <el-button type="primary" size="small" style="width: 100%">Log Out</el-button>
              </el-dropdown-item>
            </el-dropdown-menu>
          </el-dropdown>
        </template>
      </div>

      <i class="mobile-menu-btn el-icon-menu" @click="mobileMenuVisible = true"></i>
    </div>

    <!-- 移动端菜单 -->
    <div class="mobile-mask" v-if="mobileMenuVisible" @click="mobileMenuVisible = false"></div>
    <div class="mobile-menu" :class="{ open: mobileMenuVisible }">
      <div class="mobile-menu-head">
        <span>Menu</span>
        <i class="el-icon-close" @click="mobileMenuVisible = false"></i>
      </div>
      <router-link v-for="item in navItems" :key="item.path" :to="item.path"
        :class="{ active: $route.path === item.path }" @click.native="mobileMenuVisible = false">
        {{ item.name }}
      </router-link>
      <router-link v-if="!isLoggedIn" to="/login" class="mobile-login-btn" @click.native="mobileMenuVisible = false">
        Login / Register
      </router-link>
      <a v-else class="mobile-login-btn" @click="mobileMenuVisible = false; logoutVisible = true">Log Out</a>
    </div>

    <el-dialog title="Prompt" :visible.sync="logoutVisible" width="360px" center :show-close="true"
      :modal="false" custom-class="logout-dialog">
      <p class="logout-tip">Confirm to log out?</p>
      <span slot="footer" class="dialog-footer">
        <el-button @click="logoutVisible = false">Cancel</el-button>
        <el-button type="primary" @click="confirmLogout">Confirm</el-button>
      </span>
    </el-dialog>

    <el-dialog title="Edit Profile" :visible.sync="profileVisible" width="560px" :show-close="true" :modal="false"
      custom-class="profile-dialog" @closed="resetProfileForm">
      <el-form label-position="top" :model="profileForm">
        <el-form-item label="Avatar">
          <ImageUpload ref="avatarUpload" v-model="profileForm.avatar" @loaded="avatarLoading = false"
            @error="avatarLoading = false" />
        </el-form-item>
        <el-form-item label="Name">
          <el-input v-model="profileForm.name" placeholder="Enter name"></el-input>
        </el-form-item>
        <el-form-item label="Company Name">
          <el-input v-model="profileForm.company" placeholder="Enter company name"></el-input>
        </el-form-item>
        <el-form-item label="Job Title">
          <el-input v-model="profileForm.title" placeholder="Enter job title"></el-input>
        </el-form-item>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button @click="profileVisible = false">Cancel</el-button>
        <el-button type="primary" :disabled="avatarLoading" :loading="savingProfile" @click="saveProfile">Save</el-button>
      </span>
    </el-dialog>
  </header>
</template>

<script>
import ImageUpload from '@/components/ImageUpload.vue'

export default {
  name: 'AppHeader',
  components: { ImageUpload },
  props: {
    transparent: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      navItems: [
        { name: 'Home', path: '/' },
        { name: 'About Us', path: '/about' },
        { name: 'News & Updates', path: '/news' }
      ],
      logoutVisible: false,
      profileVisible: false,
      mobileMenuVisible: false,
      // 头像图片是否正在加载（加载完成前禁用 Save）
      avatarLoading: false,
      // 保存资料接口请求中
      savingProfile: false,
      profileForm: {
        avatar: '',
        name: '',
        company: '',
        title: ''
      }
    }
  },
  computed: {
    isLoggedIn() {
      return this.$store.getters.isLoggedIn
    },
    userInfo() {
      return this.$store.getters.userInfo || {}
    }
  },
  methods: {
    handleCommand(command) {
      if (command === 'logout') {
        this.logoutVisible = true
      } else if (command === 'profile') {
        this.openProfile()
      }
    },
    // 打开编辑资料弹框，回显当前用户信息
    openProfile() {
      this.profileForm = {
        avatar: this.userInfo.avatar || '',
        name: this.userInfo.nickname || this.userInfo.name || '',
        company: this.userInfo.company_name || this.userInfo.company || '',
        title: this.userInfo.position || this.userInfo.title || ''
      }
      this.profileVisible = true
      // 有头像地址时，先禁用保存，等图片加载完成后再开放
      this.avatarLoading = !!this.profileForm.avatar
      // 弹框内组件渲染后，若图片已缓存加载完成则立即解除禁用
      this.$nextTick(() => {
        const up = this.$refs.avatarUpload
        if (this.profileForm.avatar && up && up.imgLoaded) {
          this.avatarLoading = false
        }
      })
    },
    // 保存资料：调用 updateUserInfo 接口，成功后更新 Vuex 与 localStorage
    async saveProfile() {
      // 图片未加载完成前不允许保存
      if (this.avatarLoading) {
        this.$message.warning('Avatar is still loading, please wait')
        return
      }
      this.savingProfile = true
      try {
        const res = await this.$api.updateUserInfo({
          nickname: this.profileForm.name,
          avatar: this.profileForm.avatar,
          company_name: this.profileForm.company,
          position: this.profileForm.title
        })
        if (!res || res.code != '1') {
          this.$message.error((res && res.msg) || 'Update failed')
          return
        }
        const updated = {
          ...this.userInfo,
          avatar: this.profileForm.avatar,
          nickname: this.profileForm.name,
          name: this.profileForm.name,
          company_name: this.profileForm.company,
          company: this.profileForm.company,
          position: this.profileForm.title,
          title: this.profileForm.title
        }
        this.$store.commit('setUserInfo', updated)
        this.profileVisible = false
        this.$message.success('Profile updated')
      } catch (e) {
        console.error('updateUserInfo failed', e)
      } finally {
        this.savingProfile = false
      }
    },
    resetProfileForm() {
      this.profileForm = { avatar: '', name: '', company: '', title: '' }
      this.avatarLoading = false
    },
    confirmLogout() {
      this.$store.commit('clearUserInfo')
      try {
        localStorage.removeItem('token')
      } catch (e) {}
      this.logoutVisible = false
      if (this.$route.path !== '/') {
        this.$router.push('/')
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.app-header {
  background: #fff;
  border-bottom: 1px solid #e8e8e8;
  position: relative;
  z-index: 100;

  &.header-transparent {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    background: transparent;
    box-shadow: none;

    .header-inner {

      .logo,
      .main-nav a,
      .login-btn {
        color: #fff;
      }

      .main-nav a {
        &.active::after {
          background: #fff;
        }

        &:hover {
          color: #fff;
        }
      }

      .login-btn {
        border-color: #fff;
      }
    }
  }
}

.header-inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 80px;
}

.logo {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 18px;
  font-weight: 600;
  color: #2e3a8f;

  .logo-img {
    width: 32px;
    height: 32px;
    object-fit: contain;
  }
}

.main-nav {
  display: flex;
  gap: 50px;

  a {
    font-size: 15px;
    color: #333;
    position: relative;
    padding: 8px 0;

    &:hover {
      color: #2e3a8f;
    }

    &.active {
      color: #2e3a8f;
      font-weight: 600;

      &::after {
        content: '';
        position: absolute;
        bottom: 0;
        left: 50%;
        transform: translateX(-50%);
        width: 24px;
        height: 3px;
        background: #2e3a8f;
        border-radius: 2px;
      }
    }
  }
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 16px;
}

.login-btn {
  padding: 8px 18px;
  border: 1px solid #2e3a8f;
  border-radius: 6px;
  color: #2e3a8f;
  font-size: 14px;
  transition: all 0.3s;

  &:hover {
    background: #2e3a8f;
    color: #fff;
  }
}

.user-info {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;

  span {
    font-size: 14px;
    color: #333;
  }
}

.dropdown-user {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 6px 0;

  .name {
    font-weight: 600;
    color: #333;
    font-size: 14px;
  }

  .edit {
    font-size: 12px;
    color: #666;
    margin-top: 4px;
  }
}

.logout-tip {
  text-align: center;
  color: #666;
  font-size: 14px;
  padding: 10px 0;
}

.mobile-menu-btn,
.mobile-mask,
.mobile-menu {
  display: none;
}

@media (max-width: 992px) {
  .header-inner {
    height: 64px;
  }

  .logo {
    font-size: 15px;

    .logo-img {
      width: 28px;
      height: 28px;
    }
  }

  .main-nav {
    gap: 24px;

    a {
      font-size: 13px;
    }
  }

  .login-btn {
    padding: 6px 12px;
    font-size: 12px;
  }
}

@media (max-width: 768px) {
  .header-inner {
    height: 56px;
  }

  .logo {
    font-size: 13px;
    gap: 8px;

    span {
      max-width: 130px;
      line-height: 1.2;
    }

    .logo-img {
      width: 26px;
      height: 26px;
    }
  }

  .main-nav,
  .header-actions {
    display: none;
  }

  .mobile-menu-btn {
    display: block;
    font-size: 22px;
    cursor: pointer;
    padding: 8px;
  }

  .header-transparent .mobile-menu-btn {
    color: #fff;
  }

  .mobile-mask {
    display: block;
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.4);
    z-index: 998;
  }

  .mobile-menu {
    display: flex;
    flex-direction: column;
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    width: 240px;
    max-width: 78vw;
    background: #fff;
    z-index: 999;
    padding: 20px;
    transform: translateX(100%);
    transition: transform 0.3s;

    &.open {
      transform: translateX(0);
    }

    .mobile-menu-head {
      display: flex;
      align-items: center;
      justify-content: space-between;
      font-size: 16px;
      font-weight: 600;
      color: #2e3a8f;
      padding-bottom: 16px;
      margin-bottom: 8px;
      border-bottom: 1px solid #e8e8e8;

      i {
        cursor: pointer;
        color: #999;
      }
    }

    >a {
      padding: 14px 4px;
      font-size: 15px;
      color: #333;
      border-bottom: 1px solid #f2f2f2;

      &.active {
        color: #2e3a8f;
        font-weight: 600;
      }

      &.mobile-login-btn {
        margin-top: 20px;
        text-align: center;
        border: 1px solid #2e3a8f;
        border-radius: 6px;
        color: #2e3a8f;
        border-bottom: 1px solid #2e3a8f;
      }
    }
  }
}
</style>
